import { z } from "zod";

import { gatekeeperPrecisionPairSchema } from "@/types/schemas/core";

/**
 * Persisted snapshot of a conversation transcript + CoT for TanStack DB (single source of truth).
 */
const syncTimestampSchema = z.union([z.date(), z.string()]);
const syncMessageErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
});

const syncMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  timestamp: syncTimestampSchema,
  status: z.enum(["created", "succeeded", "failed", "cancelled"]).optional(),
  error: syncMessageErrorSchema.optional(),
  completionDurationMs: z.number().optional(),
  userIntent: z.enum(["question", "precision"]).optional(),
  gatekeeperPrecisions: z.array(gatekeeperPrecisionPairSchema).optional(),
});

const syncChainOfThoughtActionSchema = z.object({
  id: z.string(),
  type: z.string(),
  content: z.string(),
  timestamp: syncTimestampSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
});

const syncMessageChainOfThoughtSchema = z.object({
  messageId: z.string(),
  messagePreview: z.string(),
  timestamp: syncTimestampSchema,
  content: z.string(),
  actions: z.array(syncChainOfThoughtActionSchema),
  agentConfigurationId: z.string().optional(),
});

const syncInProgressMessageSchema = z.object({
  sId: z.string(),
  snapshotContent: z.string(),
});

export const conversationContentRowSchema = z.object({
  conversationId: z.string(),
  messages: z.array(syncMessageSchema),
  allChainOfThoughts: z.array(syncMessageChainOfThoughtSchema),
  lastAgentConfigurationId: z.string().nullable(),
  inProgressMessage: syncInProgressMessageSchema.nullable(),
  /** When true, next open triggers a network refresh. */
  stale: z.boolean(),
  lastFetchedAt: z.string(),
});

export type ConversationContentRow = z.infer<
  typeof conversationContentRowSchema
>;
